/**
 * Ads Management API endpoints
 */
import { api } from './baseApi';

interface PaginatedResponse<T> {
  success: boolean;
  count: number;
  next: string | null;
  previous: string | null;
  total_pages: number;
  current_page: number;
  results: T[];
}

export interface Ad {
  id: number;
  title: string;
  title_ar: string;
  description: string;
  description_ar: string;
  image: string | null;
  video: string | null;
  link_url: string | null;
  link_text: string;
  link_text_ar: string;
  is_active: boolean;
  is_currently_active?: boolean;
  order: number;
  start_date: string | null;
  end_date: string | null;
  views_count: number;
  clicks_count: number;
  created_at: string;
  updated_at: string;
}

interface AdResponse {
  success: boolean;
  data: Ad;
  message?: string;
}

interface AdListParams {
  is_active?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
}

export const adsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Public endpoints (no auth required)
    getAds: builder.query<PaginatedResponse<Ad>, { page?: number; page_size?: number }>({
      query: (params) => ({
        url: '/ads/',
        params,
      }),
      providesTags: ['Ads'],
    }),
    getAd: builder.query<AdResponse, number>({
      query: (id) => `/ads/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Ad', id }],
    }),
    incrementAdClick: builder.mutation<{ success: boolean; data: { clicks_count: number }; message: string }, number>({
      query: (id) => ({
        url: `/ads/${id}/click/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Ad', id }],
    }),

    // Admin endpoints (auth required)
    getAdsAdmin: builder.query<PaginatedResponse<Ad>, AdListParams>({
      query: (params) => ({
        url: '/dashboard/ads/',
        params: {
          ...params,
          is_active: params.is_active,
        },
      }),
      providesTags: ['Ads'],
    }),
    getAdAdmin: builder.query<AdResponse, number>({
      query: (id) => `/dashboard/ads/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Ad', id }],
    }),
    createAd: builder.mutation<AdResponse, FormData>({
      query: (formData) => ({
        url: '/dashboard/ads/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Ads'],
    }),
    updateAd: builder.mutation<AdResponse, { id: number; data: FormData }>({
      query: ({ id, data }) => ({
        url: `/dashboard/ads/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Ad', id }, 'Ads'],
    }),
    deleteAd: builder.mutation<void, number>({
      query: (id) => ({
        url: `/dashboard/ads/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Ads'],
    }),
  }),
});

export const {
  useGetAdsQuery,
  useGetAdQuery,
  useIncrementAdClickMutation,
  useGetAdsAdminQuery,
  useGetAdAdminQuery,
  useCreateAdMutation,
  useUpdateAdMutation,
  useDeleteAdMutation,
} = adsApi;

